import { apiUrl } from "../constans";
import apiClient from '../libs/axios';

/**
 * Mensaje legible de un error de la API: primer error de validación (422)
 * o el mensaje general del backend.
 */
export const getErrorMessage = (error, fallback = 'Error desconocido') => {
    const body = error?.response?.data;
    if (!body) return error?.message || fallback;

    const errors = body.errors || body.errorMessage;
    if (errors && typeof errors === 'object') {
        const first = Object.values(errors).flat()[0];
        if (first) return first;
    }
    if (typeof body.message === 'string' && body.message) return body.message;
    if (typeof body.data === 'string' && body.data) return body.data;
    return fallback;
};

const pqr = {
    namespaced: true,
    state: {
        pqrs: {
            data: [],
            current_page: 1,
            per_page: 15,
            last_page: 1,
            total: 0
        },
        pqrDetail: null,
        loading: false,
        error: null,
    },

    mutations: {
        setPQRs(state, pqrs) {
            state.pqrs.data = pqrs.data;
            state.pqrs.current_page = pqrs.current_page;
            state.pqrs.per_page = pqrs.per_page;
            state.pqrs.total = pqrs.total;
            state.pqrs.last_page = pqrs.last_page;
        },

        setPQRDetail(state, pqr) {
            state.pqrDetail = pqr;
        },

        updatePQR(state, updatedPqr) {
            const index = state.pqrs.data.findIndex((item) => item.id === updatedPqr.id);
            if (index !== -1) {
                state.pqrs.data[index] = updatedPqr;
            }
            state.pqrDetail = updatedPqr;
        },

        removePQR(state, pqrId) {
            state.pqrs.data = state.pqrs.data.filter((item) => item.id !== pqrId);
        },

        setLoading(state, loading) {
            state.loading = loading;
        },

        setError(state, error) {
            state.error = error;
        },

        clearError(state) {
            state.error = null;
        },

        clearPQRDetail(state) {
            state.pqrDetail = null;
        },
    },

    getters: {
        getPQRs(state) {
            return state.pqrs;
        },

        getPQRDetail(state) {
            return state.pqrDetail;
        },

        getLoading(state) {
            return state.loading;
        },

        getError(state) {
            return state.error;
        },
    },

    actions: {
        /**
         * Obtener lista de PQRs con filtros y paginación
         */
        async getPQRsAction({ commit }, { filters = {}, page = 1 }) {
            commit('setLoading', true);
            commit('clearError');

            try {
                const params = new URLSearchParams({
                    page: page,
                    per_page: filters.per_page || 15,
                });

                // Agregar filtros opcionales (v-select devuelve objetos {label, value})
                if (filters.categoria) params.append('categoria', filters.categoria.value || filters.categoria);
                if (filters.estado) params.append('estado', filters.estado.value || filters.estado);
                if (filters.prioridad) params.append('prioridad', filters.prioridad.value || filters.prioridad);
                if (filters.search) params.append('search', filters.search);

                const response = await apiClient.get(`${apiUrl}/auth/pqr?${params}`);
                const pqrsData = response.data.data || response.data;

                commit('setPQRs', pqrsData);
                commit('setLoading', false);
                return pqrsData;
            } catch (error) {
                console.error('Error fetching PQRs:', error);
                commit('setError', error.response?.data?.message || 'Error al cargar PQRs');
                commit('setLoading', false);
                throw error;
            }
        },

        /**
         * Obtener detalle completo de un PQR
         */
        async getPQRDetailAction({ commit }, pqrId) {
            commit('setLoading', true);
            commit('clearError');

            try {
                const response = await apiClient.get(`${apiUrl}/auth/pqr/${pqrId}`);
                const pqrData = response.data.data || response.data;

                commit('setPQRDetail', pqrData);
                commit('setLoading', false);
                return pqrData;
            } catch (error) {
                console.error('Error fetching PQR detail:', error);
                commit('setError', error.response?.data?.message || 'Error al cargar detalle del PQR');
                commit('setLoading', false);
                throw error;
            }
        },

        /**
         * Actualizar PQR (editar información general)
         */
        async updatePQRAction({ commit }, { pqrId, data }) {
            commit('setLoading', true);
            commit('clearError');

            try {
                const response = await apiClient.put(`${apiUrl}/auth/pqr/${pqrId}`, data);
                const updatedPqr = response.data.data || response.data;

                commit('updatePQR', updatedPqr);
                commit('setLoading', false);
                return updatedPqr;
            } catch (error) {
                console.error('Error updating PQR:', error);
                commit('setError', error.response?.data?.message || 'Error al actualizar PQR');
                commit('setLoading', false);
                throw error;
            }
        },

        /**
         * Cambiar estado del PQR
         */
        async updatePQRStateAction({ commit }, { pqrId, estado, respuesta_admin }) {
            commit('setLoading', true);
            commit('clearError');

            try {
                const response = await apiClient.put(`${apiUrl}/auth/pqr/${pqrId}/estado`, {
                    estado,
                    respuesta_admin,
                });
                const updatedPqr = response.data.data || response.data;

                commit('updatePQR', updatedPqr);
                commit('setLoading', false);
                return updatedPqr;
            } catch (error) {
                console.error('Error updating PQR state:', error);
                commit('setError', error.response?.data?.message || 'Error al cambiar estado del PQR');
                commit('setLoading', false);
                throw error;
            }
        },

        /**
         * Responder PQR. respuesta_admin es el HTML del editor, con las imágenes ya subidas
         * dentro como <img src="url">. Ver docs/pqr-respuesta-contrato-api.md del backend
         */
        async responderPQRAction({ commit }, { pqrId, respuesta_admin, estado }) {
            commit('setLoading', true);
            commit('clearError');

            try {
                const response = await apiClient.post(`${apiUrl}/auth/pqr/${pqrId}/responder`, {
                    respuesta_admin,
                    estado,
                });
                const updatedPqr = response.data.data || response.data;

                commit('updatePQR', updatedPqr);
                commit('setLoading', false);
                return updatedPqr;
            } catch (error) {
                console.error('Error responding PQR:', error);
                commit('setError', getErrorMessage(error, 'Error al responder el PQR'));
                commit('setLoading', false);
                throw error;
            }
        },

        /**
         * Subir una imagen del editor de respuesta. Devuelve { id, url, tipo }
         */
        // eslint-disable-next-line no-empty-pattern
        async uploadPQRImageAction({}, { pqrId, file, tipo = 'respuesta' }) {
            const fd = new FormData();
            fd.append('image', file);
            fd.append('tipo', tipo);

            const response = await apiClient.post(`${apiUrl}/auth/pqr/${pqrId}/imagenes`, fd, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            return response.data.data;
        },

        /**
         * Quitar una imagen subida (por ejemplo, la que el admin borró del editor antes de enviar)
         */
        // eslint-disable-next-line no-empty-pattern
        async deletePQRImageAction({}, { pqrId, imagenId }) {
            await apiClient.delete(`${apiUrl}/auth/pqr/${pqrId}/imagenes/${imagenId}`);
        },

        /**
         * Mejorar el borrador de respuesta con IA. Acepta el HTML del editor y devuelve HTML
         * con las mismas <img>. No guarda nada.
         */
        // eslint-disable-next-line no-empty-pattern
        async mejorarRespuestaIAAction({}, { texto, asunto, descripcion, categoria }) {
            const response = await apiClient.post(`${apiUrl}/v2/ai-pqrs/mejorar`, {
                tipo: 'respuesta',
                texto,
                asunto,
                descripcion: descripcion ? descripcion.slice(0, 5000) : null,
                categoria,
            });
            return response.data.data;
        },

        /**
         * Eliminar PQR
         */
        async deletePQRAction({ commit }, pqrId) {
            commit('setLoading', true);
            commit('clearError');

            try {
                await apiClient.delete(`${apiUrl}/auth/pqr/${pqrId}`);

                commit('removePQR', pqrId);
                commit('setLoading', false);
                return { success: true };
            } catch (error) {
                console.error('Error deleting PQR:', error);
                commit('setError', error.response?.data?.message || 'Error al eliminar PQR');
                commit('setLoading', false);
                throw error;
            }
        },
    },
};

export default pqr;
